/**
 * Proactive Version Check
 *
 * Polls /version.json to detect new deploys and notify the user.
 * Only runs in production — dev server uses HMR instead.
 *
 * Flow:
 * 1. On init, read the embedded build version (VITE_BUILD_VERSION)
 * 2. Every POLL_INTERVAL ms, fetch /version.json from the server
 * 3. If the server version differs from the embedded version, dispatch
 *    a custom "app-update-available" event on window
 * 4. UpdateBanner component listens for this event and shows the UI
 */

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const EVENT_NAME = 'app-update-available';

let pollTimer: ReturnType<typeof setInterval> | null = null;
let updateAvailable = false;

/**
 * Fetch the current server version
 */
async function fetchServerVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version || null;
  } catch {
    // Network error, offline, etc. — silently skip this cycle
    return null;
  }
}

/**
 * Check if an update is available and dispatch event if so
 */
async function checkForUpdate(): Promise<void> {
  if (updateAvailable) return; // Already notified, stop checking

  const currentVersion = import.meta.env.VITE_BUILD_VERSION;
  if (!currentVersion) return; // No embedded version (dev mode)

  const serverVersion = await fetchServerVersion();
  if (!serverVersion) return; // Couldn't reach server

  if (serverVersion !== currentVersion) {
    updateAvailable = true;
    window.dispatchEvent(new CustomEvent(EVENT_NAME, {
      detail: { currentVersion, serverVersion },
    }));

    // Stop polling — we've already detected the update
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }
}

/**
 * Initialize the version check poller.
 * Call once on app startup (main.tsx).
 */
export function initVersionCheck(): void {
  // Only run in production
  if (!import.meta.env.PROD) return;

  // Start polling after initial delay (don't check immediately on page load)
  const initialDelay = 30 * 1000; // 30 seconds after load
  setTimeout(() => {
    checkForUpdate();
    pollTimer = setInterval(checkForUpdate, POLL_INTERVAL);
  }, initialDelay);
}

/**
 * Event name for listening to update notifications.
 */
export const UPDATE_EVENT = EVENT_NAME;

/**
 * Check if an update has already been detected.
 */
export function isUpdateAvailable(): boolean {
  return updateAvailable;
}
